import Box from 'assets/components/box/Box';
import DataMap from 'assets/components/dataMap/DataMap';
import { addLog } from 'assets/components/feedback/Feedback';
import { useModalForm } from 'assets/components/form/modalForm/ModalForm';
import CheckInput from 'assets/components/inputs/check/CheckInput';
import { inputStyles } from 'assets/components/inputs/Input';
import CheckWrapper from 'assets/components/inputs/wrappers/CheckWrapper';
import FormWrapper from 'assets/components/inputs/wrappers/FormWrapper';
import { getLocales } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import { HttpStatus } from 'config/Api.Config';
import { isEmpty } from 'lodash';
import claimTypeDealerGroupAutomationRuleDeleteApi from 'models/dealerManagement/claimTypeDealerGroupAutomationRule/delete/Api.ClaimTypeDealerGroupAutomationRule.Delete';
import AutomationReadonlySummary from 'module/automation/components/Automation.ReadonlySummary';
import AutomationRulesTable from 'module/automation/components/Automation.Rule.Table';
import { createRule } from 'module/automation/helpers/ruleHelpers';
import { createDealerGroupClaimAutomationSchema } from 'module/automation/schemas/ClaimAutomationSchema';
import useClaimStatuses from 'module/claims/hooks/useClaimStatuses';
import React, { Fragment, useEffect, useState } from 'react';
import { useDealerGroupProvider } from '../../DealerGroup.Hooks';
import { DealerGroupForm } from '../DealerGroup.Edit';

const DealerGroupClaimTypeAutomationRules: React.FC = () => {
  const { lang } = getLocales();
  const { form } = useModalForm<DealerGroupForm>();
  const { claimTypesPayload } = useDealerGroupProvider();
  const { automationClaimStatuses, automationClaimJobStatuses } = useClaimStatuses();
  const currentRules = form.data?.claimTypeDealerGroupAutomationRules ?? [];
  const schema = createDealerGroupClaimAutomationSchema({
    claimStatuses: automationClaimStatuses,
    claimJobStatuses: automationClaimJobStatuses,
  });

  const updateRule = (id: number, newData: Partial<Model.IClaimTypeDealerGroupAutomationRule>, claimTypeId: number) => {
    const updated = currentRules.map((entry) =>
      entry.claimTypeAutomationRuleId === id
        ? {
            ...entry,
            ...newData,
            claimTypeAutomationRule: {
              claimTypeId,
            },
          }
        : entry
    );
    form.update({ claimTypeDealerGroupAutomationRules: updated });
  };

  const updateEnabled = (claimTypeId: number, newData: { isClaimTypeAutomationEnabled: any }) => {
    const currentDealerClaimTypes = [...(form.data.dealerGroupClaimTypes || [])];
    const updated = currentDealerClaimTypes.map((entry) =>
      entry.claimTypeId === claimTypeId ? { ...entry, ...newData } : entry
    );
    form.update({ dealerGroupClaimTypes: updated });
  };

  const deleteRule = async (claimTypeRuleId: number, ruleId?: number, claimTypeId?: number) => {
    const updatedRules = form.data?.claimTypeDealerGroupAutomationRules?.filter(
      (it) => it.claimTypeAutomationRuleId !== claimTypeRuleId
    );
    form.update({ claimTypeDealerGroupAutomationRules: updatedRules });
    setOverrideClaimRule((prev) => ({
      ...prev,
      [claimTypeId]: false,
    }));

    if (ruleId) {
      const res = await claimTypeDealerGroupAutomationRuleDeleteApi({ id: ruleId });
      if (res.status !== HttpStatus.ok) {
        addLog({ error: res.message });
      }
    }
  };

  const [overrideClaimRule, setOverrideClaimRule] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (isEmpty(overrideClaimRule)) {
      const initialOverrides: Record<string, boolean> = {};

      claimTypesPayload?.data?.forEach((claimType) => {
        const automationRuleIds = claimType.claimTypeAutomationRules?.map((r) => r.id) ?? [];
        const hasDealerOverride = (form.data?.claimTypeDealerGroupAutomationRules ?? []).some((dealerRule) =>
          automationRuleIds.includes(dealerRule.claimTypeAutomationRuleId)
        );

        if (hasDealerOverride) initialOverrides[claimType.id] = hasDealerOverride;
      });

      setOverrideClaimRule(initialOverrides);
    }
  }, [claimTypesPayload, form.data?.claimTypeDealerGroupAutomationRules]);

  return (
    <FormWrapper>
      <DataMap
        data={claimTypesPayload?.data}
        render={({ data: claimType }) => {
          const dealerGroupClaimType = form.data?.dealerGroupClaimTypes?.find((it) => it.claimTypeId === claimType.id);

          return (
            !!claimType.claimTypeAutomationRules.length &&
            dealerGroupClaimType && (
              <Fragment key={claimType.id}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: Theme.Size.M }}>
                    <h3>{claimType.name}</h3>
                    <CheckWrapper>
                      <CheckInput
                        name="isClaimTypeAutomationEnabled"
                        label={lang.enabled}
                        value={dealerGroupClaimType?.isClaimTypeAutomationEnabled}
                        onChange={({ isClaimTypeAutomationEnabled }) =>
                          updateEnabled(claimType.id, { isClaimTypeAutomationEnabled })
                        }
                        class={inputStyles.check.switch}
                        htmlElementProps={{
                          title: dealerGroupClaimType?.isClaimTypeAutomationEnabled ? lang.disable : lang.enable,
                        }}
                      />
                    </CheckWrapper>
                  </div>
                </div>
                &nbsp;
                {dealerGroupClaimType?.isClaimTypeAutomationEnabled && (
                  <DataMap
                    data={claimType.claimTypeAutomationRules}
                    render={({ data: rule }) => {
                      const currentRule = currentRules.find((c) => c.claimTypeAutomationRuleId === rule.id);

                      return (
                        <div key={rule.id}>
                          <Box style={{ display: 'flex', flexDirection: 'column', marginBottom: '1rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                              <h3>{lang.claimRule}</h3>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: Theme.Size.M }}>
                              <AutomationReadonlySummary rule={rule} schema={schema} />
                              <div style={{ alignSelf: 'flex-start' }}>
                                <CheckWrapper>
                                  <CheckInput
                                    name="overrideClaimRule"
                                    label={lang.overrideClaimRule}
                                    value={overrideClaimRule[claimType.id]}
                                    onChange={({ overrideClaimRule: isOverrided }) => {
                                      setOverrideClaimRule((prev) => ({ ...prev, [claimType.id]: isOverrided }));
                                      const currentRules = form.data?.claimTypeDealerGroupAutomationRules || [];

                                      if (isOverrided) {
                                        const alreadyExists = currentRules.some(
                                          (it) => it.claimTypeAutomationRuleId === rule.id
                                        );
                                        if (alreadyExists) return;

                                        const newEntry = createRule(schema, {
                                          claimTypeId: claimType.id,
                                          claimTypeAutomationRuleId: rule.id,
                                          claimTypeAutomationRule: {
                                            claimTypeId: claimType.id,
                                          },
                                        }) as Model.IClaimTypeDealerGroupAutomationRule;

                                        form.update({
                                          claimTypeDealerGroupAutomationRules: [...currentRules, newEntry],
                                        });
                                      } else {
                                        const filtered = currentRules.filter(
                                          (r) => r.claimTypeAutomationRuleId !== rule.id
                                        );
                                        form.update({ claimTypeDealerGroupAutomationRules: filtered });
                                      }
                                    }}
                                    disabled={(form.data?.claimTypeDealerGroupAutomationRules ?? []).some(
                                      (it) => it.claimTypeAutomationRuleId === rule.id && it.id
                                    )}
                                    class={inputStyles.check.radio}
                                  />
                                </CheckWrapper>
                              </div>
                            </div>
                          </Box>
                          {overrideClaimRule[claimType.id] && currentRule && (
                            <div>
                              <h3>{lang.overrideClaimRule}</h3>
                              <AutomationRulesTable<Model.IClaimTypeDealerGroupAutomationRule>
                                value={[currentRule as Model.IClaimTypeDealerGroupAutomationRule]}
                                schema={schema}
                                hideAddRule
                                onChange={(rules) => updateRule(rule.id, rules[0], claimType.id)}
                                onDeleteRule={() => deleteRule(rule.id, currentRule?.id, claimType?.id)}
                              />
                            </div>
                          )}
                        </div>
                      );
                    }}
                  />
                )}
                <br />
              </Fragment>
            )
          );
        }}
      />
    </FormWrapper>
  );
};

export default DealerGroupClaimTypeAutomationRules;
